import ExcelJS from "exceljs";

export const generateExcel = (data, fileName) => {
  const workbook = new ExcelJS.Workbook();
  const normalizedFileName = String(fileName || "").toLowerCase();
  const shouldApplyMonthYearTextFormat =
    normalizedFileName.includes("eoltreatmentofsoldproducts");
  const shouldApplyUseOfSoldProductsRules =
    normalizedFileName.includes("useofsoldproducts");

  const applyTextFormatForMonthYearColumns = (worksheet, columns) => {
    if (!shouldApplyMonthYearTextFormat) return;

    const textHeaders = new Set(
      (columns || [])
        .map((c) => c?.header)
        .filter(Boolean)
        .filter(
          (h) =>
            String(h).includes("MM/YYYY") ||
            String(h).includes("Month (in 01/25 or Jan-25)")
        )
    );
    if (textHeaders.size === 0) return;

    (columns || []).forEach((col, idx) => {
      const header = col?.header;
      if (!header || !textHeaders.has(header)) return;

      const column = worksheet.getColumn(idx + 1);
      // Force Excel to treat this whole column as text.
      column.numFmt = "@";

      // Ensure existing cells are stored as strings.
      column.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
        if (rowNumber === 1) return; // header row
        if (cell.value === null || cell.value === undefined) return;
        cell.value = String(cell.value);
      });
    });
  };

  const applyUseOfSoldProductsTemplateRules = (worksheet, columns) => {
    if (!shouldApplyUseOfSoldProductsRules) return;

    const recyclableIndex =
      (columns || []).findIndex(
        (col) => col?.header === "Recyclable Components (%)"
      ) + 1;

    if (recyclableIndex <= 0) return;

    for (let rowNumber = 2; rowNumber <= 1000; rowNumber += 1) {
      const cell = worksheet.getCell(rowNumber, recyclableIndex);
      cell.dataValidation = {
        type: "decimal",
        operator: "between",
        allowBlank: true,
        formulae: [0, 100],
        showErrorMessage: true,
        errorTitle: "Invalid percentage",
        error: "Enter a value between 0 and 100.",
      };
    }
  };

  // Check if data is an array of sheet objects or just a single data array
  if (Array.isArray(data) && data.length > 0 && data[0].name && data[0].data) {
    // Multiple sheets structure: [{name: "Sheet1", data: [...]}, {name: "Sheet2", data: [...]}]

    data.forEach(({ name, data: sheetData }, sheetIndex) => {

      if (sheetData && Array.isArray(sheetData) && sheetData.length > 0) {
        const worksheet = workbook.addWorksheet(name);
        const columns = Object.keys(sheetData[0]).map((key) => ({ header: key, key }));
        worksheet.columns = columns;

        sheetData.forEach((item) => worksheet.addRow(item));
        applyTextFormatForMonthYearColumns(worksheet, columns);
        applyUseOfSoldProductsTemplateRules(worksheet, columns);

        // Style headers
        columns.forEach((col, index) => {
          const headerCell = worksheet.getCell(1, index + 1);
          headerCell.font = { bold: true, color: { argb: "FFFFFF" }, size: 12 };
          headerCell.alignment = { horizontal: "center", vertical: "middle" };
          headerCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "4F81BD" },
          };
        });

        // Auto column width - fix the undefined values issue
        worksheet.columns.forEach((col, index) => {
          if (col && col.key) {
            // Get all values for this column including header and data
            const headerLength = String(col.header || "").length;
            const dataValues = sheetData.map(row => String(row[col.key] || "")).filter(Boolean);
            const maxDataLength = dataValues.length > 0 ? Math.max(...dataValues.map(val => val.length)) : 0;
            const maxLength = Math.max(headerLength, maxDataLength);
            col.width = maxLength + 2;
          }
        });
      }
    });
  } else {
    // Single sheet structure: just an array of data objects
    const worksheet = workbook.addWorksheet("data");

    const columns = data && Object.keys(data?.[0] || {}).map((key) => ({ header: key, key }));
    worksheet.columns = columns;

    data.forEach((item) => worksheet.addRow(item));
    applyTextFormatForMonthYearColumns(worksheet, columns);
    applyUseOfSoldProductsTemplateRules(worksheet, columns);

    // Style headers
    columns.forEach((col, index) => {
      const headerCell = worksheet.getCell(1, index + 1);
      headerCell.font = { bold: true, color: { argb: "FFFFFF" }, size: 12 };
      headerCell.alignment = { horizontal: "center", vertical: "middle" };
      headerCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4F81BD" },
      };
    });

    // Auto column width - fix the undefined values issue
    worksheet.columns.forEach((col, index) => {
      if (col && col.key) {
        // Get all values for this column including header and data
        const headerLength = String(col.header || "").length;
        const dataValues = data.map(row => String(row[col.key] || "")).filter(Boolean);
        const maxDataLength = dataValues.length > 0 ? Math.max(...dataValues.map(val => val.length)) : 0;
        const maxLength = Math.max(headerLength, maxDataLength);
        col.width = maxLength + 2;
      }
    });
  }

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.xlsx`;
    link.click();
  });
};

export default generateExcel;
